import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { adminAPI } from '../../utils/adminAPI';
import { toast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, Upload } from 'lucide-react';
// removed duplicate React import
const FitmentEditor = ({ value, onChange }) => {
  const [region, setRegion] = useState('GCC');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [trim, setTrim] = useState('');
  const [engine, setEngine] = useState('');

  const add = () => {
    if (!region || !make || !model || !year || !trim || !engine) return;
    const next = [...value, { region, make, model, year: parseInt(year), trim, engine }];
    onChange(next);
    setMake(''); setModel(''); setYear(''); setTrim(''); setEngine('');
  };

  const remove = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <div>
          <Label>Region</Label>
          <select className="w-full border rounded px-2 py-2 mt-1" value={region} onChange={e=>setRegion(e.target.value)}>
            <option value="GCC">GCC</option>
            <option value="USA">USA</option>
          </select>
        </div>
        <div>
          <Label>Make</Label>
          <Input value={make} onChange={e=>setMake(e.target.value.toUpperCase())} placeholder="TOYOTA" className="mt-1" />
        </div>
        <div>
          <Label>Model</Label>
          <Input value={model} onChange={e=>setModel(e.target.value)} placeholder="Camry" className="mt-1" />
        </div>
        <div>
          <Label>Year</Label>
          <Input type="number" value={year} onChange={e=>setYear(e.target.value)} placeholder="2021" className="mt-1" />
        </div>
        <div>
          <Label>Trim</Label>
          <Input value={trim} onChange={e=>setTrim(e.target.value)} placeholder="SE" className="mt-1" />
        </div>
        <div>
          <Label>Engine</Label>
          <Input value={engine} onChange={e=>setEngine(e.target.value)} placeholder="2.5L" className="mt-1" />
        </div>
      </div>
      <div>
        <Button type="button" onClick={add} className="bg-blue-600 hover:bg-blue-700">Add Fitment</Button>
      </div>

      {value?.length > 0 && (
        <div className="space-y-2">
          {value.map((f, idx) => (
            <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded text-sm">
              <span>{f.region} • {f.year} {f.make} {f.model} • {f.trim} • {f.engine}</span>
              <Button type="button" size="sm" variant="ghost" onClick={()=>remove(idx)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};



const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ProductForm = ({ mode = 'create', existingProduct = null }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    part_number: '',
    price: '',
    original_price: '',
    category: '',
    image: '',
    images: [],
    rating: '4.5',
    reviews: '0',
    in_stock: true,
    stock_quantity: '100',
    weight_kg: '1.0',
    description: '',
    specifications: {},
    compatibility: [],
    fitment: []
  });

  const [specKey, setSpecKey] = useState('');
  const [specValue, setSpecValue] = useState('');
  const [compatibilityInput, setCompatibilityInput] = useState('');

  useEffect(() => {
    fetchBrandsAndCategories();
  }, []);

  useEffect(() => {
    if (existingProduct) {
      setFormData({
        ...existingProduct,
        price: existingProduct.price.toString(),
        original_price: existingProduct.original_price?.toString() || '',
        rating: existingProduct.rating.toString(),
        reviews: existingProduct.reviews.toString(),
        stock_quantity: existingProduct.stock_quantity.toString(),
        weight_kg: existingProduct.weight_kg?.toString() || '1.0',
        specifications: existingProduct.specifications || {},
        compatibility: existingProduct.compatibility || [],
        images: existingProduct.images || []
      });
    }
  }, [existingProduct]);

  const fetchBrandsAndCategories = async () => {
    try {
      // Fetch brands
      const brandsResponse = await fetch(`${BACKEND_URL}/api/brands`);
      if (brandsResponse.ok) {
        const brandsData = await brandsResponse.json();
        setBrands(brandsData);
      }

      // Fetch categories
      const categoriesResponse = await fetch(`${BACKEND_URL}/api/categories`);
      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData);
      }
    } catch (error) {
      console.error('Error fetching brands and categories:', error);
      toast({
        title: "Error",
        description: "Failed to load brands and categories",
        variant: "destructive"
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${BACKEND_URL}/api/admin/upload-image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataUpload
        });

        if (!response.ok) {
          throw new Error('Failed to upload image');
        }

        const result = await response.json();
        uploadedUrls.push(`${BACKEND_URL}${result.file_url}`);
      }

      // Update form data with new image URLs
      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...uploadedUrls],
        image: prev.image || uploadedUrls[0] // Set first image as primary if no primary exists
      }));

      toast({
        title: "Success",
        description: `${uploadedUrls.length} image(s) uploaded successfully`
      });

    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (indexToRemove) => {
    setFormData(prev => {
      const updatedImages = prev.images.filter((_, index) => index !== indexToRemove);
      return {
        ...prev,
        images: updatedImages,
        image: updatedImages.length > 0 ? updatedImages[0] : ''
      };
    });
  };

  const addSpecification = () => {
    if (specKey.trim() && specValue.trim()) {
      setFormData(prev => ({
        ...prev,
        specifications: {
          ...prev.specifications,
          [specKey]: specValue
        }
      }));
      setSpecKey('');
      setSpecValue('');
    }
  };

  const removeSpecification = (key) => {
    setFormData(prev => ({
      ...prev,
      specifications: Object.fromEntries(
        Object.entries(prev.specifications).filter(([k]) => k !== key)
      )
    }));
  };

  const addCompatibility = () => {
    if (compatibilityInput.trim()) {
      setFormData(prev => ({
        ...prev,
        compatibility: [...prev.compatibility, compatibilityInput.trim()]
      }));
      setCompatibilityInput('');
    }
  };

  const removeCompatibility = (index) => {
    setFormData(prev => ({
      ...prev,
      compatibility: prev.compatibility.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        rating: parseFloat(formData.rating),
        reviews: parseInt(formData.reviews),
        stock_quantity: parseInt(formData.stock_quantity),
        weight_kg: parseFloat(formData.weight_kg)
      };

      if (mode === 'create') {
        await adminAPI.createProduct(submitData);
        toast({
          title: 'Success',
          description: 'Product created successfully',
        });
      } else {
        await adminAPI.updateProduct(existingProduct.id, submitData);
        toast({
          title: 'Success',
          description: 'Product updated successfully',
        });
      }

      navigate('/admin/products');
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${mode === 'create' ? 'create' : 'update'} product`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/products')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Products
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">
          {mode === 'create' ? 'Add New Product' : 'Edit Product'}
        </h1>
      </div>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="part_number">Part Number</Label>
                <Input
                  id="part_number"
                  name="part_number"
                  value={formData.part_number}
                  onChange={handleChange}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="brand">Brand *</Label>
                <Select value={formData.brand} onValueChange={(value) => setFormData(prev => ({ ...prev, brand: value }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map(brand => (
                      <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(category => (
                      <SelectItem key={category.id} value={category.name}>{category.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="price">Price (OMR) *</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="original_price">Original Price (OMR)</Label>
                <Input
                  id="original_price"
                  name="original_price"
                  type="number"
                  step="0.01"
                  value={formData.original_price}
                  onChange={handleChange}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="stock_quantity">Stock Quantity *</Label>
                <Input
                  id="stock_quantity"
                  name="stock_quantity"
                  type="number"
                  value={formData.stock_quantity}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="weight_kg">Weight (KG)</Label>
                <Input
                  id="weight_kg"
                  name="weight_kg"
                  type="number"
                  step="0.1"
                  value={formData.weight_kg}
                  onChange={handleChange}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <Label>Product Images</Label>
              <div className="space-y-4">
                {/* Upload Button */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-orange-500 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                    disabled={uploadingImages}
                  />
                  <label htmlFor="image-upload" className="cursor-pointer">
                    <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-600">
                      {uploadingImages ? 'Uploading...' : 'Click to upload images or drag and drop'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 10MB each</p>
                  </label>
                </div>

                {/* Image Preview Grid */}
                {formData.images && formData.images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.images.map((imageUrl, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={imageUrl}
                          alt={`Product ${index + 1}`}
                          className="w-full h-24 object-cover rounded border"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                        {index === 0 && (
                          <div className="absolute bottom-1 left-1 bg-orange-600 text-white text-xs px-2 py-1 rounded">
                            Primary
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Fallback Image URL Input */}
                <div>
                  <Label htmlFor="image">Or paste image URL</Label>
                  <Input
                    id="image"
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    placeholder="https://example.com/image.jpg"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                rows={4}
                value={formData.description}
                onChange={handleChange}
                className="mt-1"
              />
            </div>

            {/* Stock Status */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="in_stock"
                name="in_stock"
                checked={formData.in_stock}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, in_stock: checked }))}
              />
              <Label htmlFor="in_stock">In Stock</Label>
            </div>

            {/* Specifications */}
            <div>
              <Label>Specifications</Label>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Specification name"
                    value={specKey}
                    onChange={(e) => setSpecKey(e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Value"
                    value={specValue}
                    onChange={(e) => setSpecValue(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="button" onClick={addSpecification}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {Object.entries(formData.specifications || {}).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                    <span><strong>{key}:</strong> {value}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSpecification(key)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Vehicle Compatibility */}
            <div>
              <Label>Vehicle Compatibility</Label>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., BMW 3 Series 2015-2020"
                    value={compatibilityInput}
                    onChange={(e) => setCompatibilityInput(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="button" onClick={addCompatibility}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {formData.compatibility?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                    <span>{item}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCompatibility(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
            {/* Fitment */}
            <div>
              <Label>Fitment (Guaranteed to fit)</Label>
              <p className="text-sm text-gray-500 mb-2">Add exact vehicle configurations that this part fits. All fields are required per entry.</p>

              <FitmentEditor
                value={formData.fitment || []}
                onChange={(next) => setFormData(prev => ({ ...prev, fitment: next }))}
              />
            </div>

              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/products')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-orange-600 hover:bg-orange-700">
                {loading ? 'Saving...' : mode === 'create' ? 'Create Product' : 'Update Product'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductForm;
